import NiceModal from "@ebay/nice-modal-react";
import dayjs, {Dayjs} from "dayjs";
import React, {FC} from "react";
import {z} from "zod";
import {EMAIL_REGEX} from "../../../../constants";
import {PaymentMethodTypes} from "../../../../enums";
import {InputUtils, toCardExpirationDate} from "../../../../helpers";
import {
  BankAccountType,
  bankAccountTypeInfoMap,
  PaymentDetails,
  PaymentType,
  SelectablePaymentMethod,
} from "../../../declarations";

const VALIDATE_NAME_REGEX = /^[a-zA-Z]+(\s[a-zA-Z]+)+$/;

type PaymentMethodOption = "DebitCard" | "ApplePay" | "GooglePay" | "ACH";

const DEFAULT_PAYMENT_METHODS_ORDER: PaymentMethodOption[] = ["ACH", "DebitCard", "ApplePay"];

const PAYMENT_METHOD_OPTION_BY_SELECTABLE_METHOD: Record<SelectablePaymentMethod, PaymentMethodOption> = {
  ach: "ACH",
  card: "DebitCard",
  apple_pay: "ApplePay",
};

interface AchFormDefaults {
  name: string;
  email: string;
  routingNumber: string;
  accountNumber: string;
  accountType: BankAccountType;
  confirmTerms: boolean;
}

interface CardFormDefaults {
  cardNumber: string;
  name: string;
  expirationMonth: string;
  expirationYear: string;
  cvv: string;
  zip: string;
  email: string;
  confirmTerms: boolean;
}

interface PaymentMethodChangeParams {
  selected: PaymentMethodOption;
  onSelect: (method: PaymentMethodOption) => void;
  achAllowed?: boolean;
  blockedAchSecondaryText?: string;
  closePaymentDialogHandler?: () => void;
  BlockedACHDialog: FC<any>;
}

const buildAchDefaults = (
  paymentDetails?: PaymentDetails,
  userName?: string,
  primaryEmail?: string,
  disableACHAccountOwnerPrePopulation?: boolean
): AchFormDefaults => {
  const bankAccountTypes = InputUtils.getEnumSelectOptions(bankAccountTypeInfoMap);

  return {
    name: paymentDetails?.ach?.account_name || (!disableACHAccountOwnerPrePopulation && userName) || "",
    email: paymentDetails?.email || primaryEmail || "",
    routingNumber: paymentDetails?.ach?.ABA || "",
    accountNumber: paymentDetails?.ach?.account_number || "",
    accountType: paymentDetails?.ach?.account_type || bankAccountTypes[0]?.value || "",
    confirmTerms: paymentDetails?.terms_and_conditions || false,
  };
};

const buildCardDefaults = (
  paymentDetails?: PaymentDetails,
  userName?: string,
  primaryEmail?: string
): CardFormDefaults => {
  const expirationDate = paymentDetails?.credit_card?.expiration_date
    ? parseExpirationDate(paymentDetails?.credit_card?.expiration_date)
    : "";
  return {
    cardNumber: paymentDetails?.credit_card?.card_number || "",
    name: paymentDetails?.credit_card?.card_holder_name || userName || "",
    expirationMonth: expirationDate ? expirationDate.format("MM") : "",
    expirationYear: expirationDate ? expirationDate.format("YYYY") : "",
    cvv: paymentDetails?.credit_card?.cvv || "",
    zip: paymentDetails?.credit_card?.zip_code || "",
    email: paymentDetails?.email || primaryEmail || "",
    confirmTerms: paymentDetails?.terms_and_conditions || false,
  };
};

const convertCardFormToPaymentDetails = (
  formValues: any,
  cardNumber: string,
  isPrepaid: boolean = false
): PaymentDetails => {
  return {
    credit_card: {
      card_number: cardNumber,
      cvv: formValues.cvv,
      address: "",
      card_holder_name: formValues.name,
      expiration_date: toCardExpirationDate(formValues.expirationMonth, formValues.expirationYear).format("MMYY"),
      zip_code: formValues.zip,
    },
    email: formValues.email || undefined,
    terms_and_conditions: formValues.confirmTerms,
    is_prepaid: isPrepaid,
  };
};

const convertACHFormToPaymentDetails = (formValues: any): PaymentDetails => {
  return {
    ach: {
      account_type: formValues.accountType,
      account_name: formValues.name,
      account_number: InputUtils.removeWhitespaces(formValues.accountNumber),
      ABA: InputUtils.removeWhitespaces(formValues.routingNumber),
      state: "",
      zip_code: "",
    },
    email: formValues.email || undefined,
    terms_and_conditions: formValues.confirmTerms,
  };
};

const parseExpirationDate = (dateString: string): Dayjs | null => {
  if (!dateString) return null;
  if (dateString.length === 4) {
    const month = parseInt(dateString.substring(0, 2), 10);
    const year = parseInt(dateString.substring(2, 4), 10) + 2000;
    return dayjs()
      .year(year)
      .month(month - 1)
      .date(1);
  }
  const parsed = dayjs(dateString);
  return parsed.isValid() ? parsed : null;
};

export const achFormSchema = z.object({
  accountType: z.string().min(1, "Please select an account type"),
  name: z
    .string()
    .min(1, "Full name is required")
    .regex(VALIDATE_NAME_REGEX, "Please enter the account owner's full name"),
  accountNumber: z
    .string()
    .regex(/^\d+$/, "Please enter a valid bank account number")
    .min(5, "Please enter a valid bank account number")
    .max(17, "Please enter a valid bank account number"),
  routingNumber: z
    .string()
    .transform((val) => val.replace(/-/g, ""))
    .pipe(
      z
        .string()
        .regex(/^\d+$/, "Please enter a valid 9-digit routing number")
        .min(9, "Please enter a valid routing number")
        .max(9, "Please enter a valid routing number")
    ),
  email: z
    .string()
    .optional()
    .refine((val) => !val || EMAIL_REGEX.test(val), {message: "Please enter a valid email address"}),
  confirmTerms: z.boolean().refine((val) => val === true, "Required field"),
});

export const parsedFormErrors = (errors: Record<string, any>, formValues: Record<string, any>) => {
  return Object.entries(errors).map(([key, error]) => {
    const fieldValue = formValues[key];
    const errorType = error?.type === "too_small" && (!fieldValue || fieldValue === "") ? "empty_field" : error?.type;
    return `${key}: ${errorType}`;
  });
};

export const blockAlphabeticInput = (
  event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
  maxLength?: number
) => {
  const target = event.target as HTMLInputElement;
  const currentLength = target.value?.length || 0;
  if (event.ctrlKey || event.metaKey || event.key.length > 1) {
    return;
  }
  if (maxLength && currentLength >= maxLength) {
    event.preventDefault();
    return;
  }
  if (/[a-zA-Z]/.test(event.key)) {
    event.preventDefault();
  }
};

const formatInput = (inputValue: any) => {
  const inputValueWithoutDashes = inputValue.replace(/-/g, "");
  const formattedValue = inputValueWithoutDashes.replace(/(.{4})/g, "$1-");
  return formattedValue.replace(/-$/, "");
};

const handlePaymentMethodChange = ({
  selected,
  onSelect,
  achAllowed = false,
  blockedAchSecondaryText = "contact_us",
  closePaymentDialogHandler,
  BlockedACHDialog,
}: PaymentMethodChangeParams) => {
  if (selected === "ACH") {
    onSelect("DebitCard");
  } else {
    if (achAllowed) {
      onSelect("ACH");
    } else {
      NiceModal.show(BlockedACHDialog, {
        secondaryButtonText: blockedAchSecondaryText,
        closePaymentDialogHandler,
      });
    }
  }
};

const getPaymentType = (currentMethodType: PaymentMethodTypes | undefined): PaymentType => {
  if (currentMethodType === "ach") {
    return "ACH";
  }
  if (currentMethodType === "apple_pay") {
    return "ApplePay";
  }
  if (currentMethodType === "google_pay") {
    return "GooglePay";
  }
  return "DebitCard";
};

const getShouldShowApplePay = (isAgent: boolean, applePayEligible?: boolean, merchantSiteId?: string): boolean => {
  const w = window as any;
  return w.ApplePaySession && !isAgent && applePayEligible && !!merchantSiteId;
};

export {
  buildAchDefaults,
  buildCardDefaults,
  convertCardFormToPaymentDetails,
  parseExpirationDate,
  convertACHFormToPaymentDetails,
  formatInput,
  handlePaymentMethodChange,
  getPaymentType,
  getShouldShowApplePay,
  DEFAULT_PAYMENT_METHODS_ORDER,
  PAYMENT_METHOD_OPTION_BY_SELECTABLE_METHOD,
};
export type {PaymentMethodOption};
